// Content for Sam Erpik — sourced from "Sam Erpik — Website Content & Structure Reference"

export const NAV_LINKS = [
  { id: "origin", label: "Timeline" },
  { id: "hats", label: "What I Do" },
  { id: "proof", label: "Ventures" },
  { id: "operate", label: "How I Operate" },
  { id: "signal", label: "Contact" },
];

export const ROUTE_LINKS = [
  { to: "/lab",     id: "lab",     label: "The Lab" },
  { to: "/writing", id: "writing", label: "Writing" },
];

export const HOOK = {
  kicker: "TECHNICAL FOUNDER · PRODUCT LEADER · BUILDER · CAT DAD =^.^=",
  // Three lines, matching the LinkedIn banner: "I build AI that / answers, sees / and decides."
  headlineA: "I build AI that",
  headlineB: "answers, sees",
  headlineC: "and decides.",
  sub:
    "From product design to engineering to go-to-market. I build and scale technology ventures end-to-end.",
};

// Section 01 — reverse-chronological career ledger (#origin).
// `to: null` renders "Now". Every fact here is sourced (Companies House, CVs,
// live Ventures stats). Pending numbers stay off until Sam approves them.
export const ORIGIN = {
  kicker: "01 — Timeline",
  title: "A decade in product.",
  titleEm: "Three times a founder.",
  intro:
    "I lead product and design at my own companies, and design for startups and enterprise. Two I co-founded; the third, Purple Luna, I run today.",
  meta: {
    location: "Brighton, UK",
    linkedin: "https://linkedin.com/in/samerpik/",
  },
  timeline: [
    {
      from: 2021,
      to: null,
      state: "current",
      role: "Founder",
      org: "Purple Luna",
      title: "An AI receptionist for small businesses.",
      desc: "Started as a digital agency. Now a product, live with clinic clients.",
      chips: [
        { label: "WhatsApp receptionist · Live", status: "live" },
        { label: "Luna Voice · In testing", status: "testing" },
      ],
      link: "/case/purpleluna",
    },
    {
      from: 2021,
      to: null,
      state: "ongoing",
      role: "UX/UI Design Consultant (Contract)",
      org: "Canada Life UK",
      title: "Design system and how design reaches code.",
      desc:
        "Alongside my ventures, I own the design system and keep design and code in sync. I also introduced a new design-to-developer handover tool and I'm modernising delivery processes.",
      chips: [{ label: "Website & portal refresh lead" }],
    },
    {
      from: 2025,
      to: 2026,
      state: "past",
      role: "Co-founder · Product & Tech Lead",
      org: "QuantumEye",
      title: "Co-founded QuantumEye.",
      desc:
        "Edge-AI computer vision for retail loss prevention, taken from whiteboard to pilots. Led product and the engineering teams in the UK and offshore.",
      chips: [{ label: "3 retail pilots" }, { label: "Retail Tech Show 2025" }],
      link: "/case/quantumeye",
    },
    {
      from: 2018,
      to: 2021,
      state: "past",
      role: "Head of Design (2020–21)",
      org: "UMA (founding team)",
      title: "From UI developer to Head of Design.",
      desc:
        "Led design across UMA's SaaS products. Before that, UX Design Lead at The Eventa Group (2019–20) and UI developer on WinMan's cloud ERP (2018–19).",
      chips: [{ label: "Eventa · booking conv. 3% → 6%" }],
    },
    {
      from: 2016,
      to: 2018,
      state: "past",
      role: "Co-founder & Product Lead",
      org: "WhotNow",
      title: "Co-founded WhotNow.",
      desc:
        "Real-time nightlife discovery for Brighton. It began as my MSc project and won a startup competition.",
      chips: [{ label: "200+ venues" }],
      link: "/case/whotnow",
    },
    {
      from: 2012,
      to: 2016,
      state: "education",
      role: "Education",
      org: "University of Sussex",
      // Education renders as mono lines (no h3): these lines, then `org`.
      lines: [
        "BSc (Hons) Informatics: Games & Multimedia Environments (2012–15)",
        "MSc Management & Entrepreneurship (2015–16)",
      ],
      desc: "Game engines first, business models second.",
      chips: [],
    },
  ],
};

export const HATS_INTRO = {
  kicker: "WHAT I DO",
  title: "Four chairs.",
  titleEm: "Same person.",
  sub:
    "Design. Engineering. Team leadership. Go-to-market. I operate across the full stack of building a company, all at once.",
};

export const HATS = [
  {
    key: "designer",
    tag: "PRODUCT DESIGNER",
    accent: "#FF6B35",
    title: "10 years designing for startups and enterprises.",
    body:
      "From ERP platforms to marketplace apps, IoT workspace management to financial services. On contract at Canada Life UK since 2021, I own the design system and turn paper-based workflows into digital experiences that actually work. I also improve what happens between design and development: a new handover tool, design and code kept aligned, and proven practices brought back in a modern form.",
    tags: ["UX Strategy", "Design Systems", "Financial Platforms", "SaaS", "Enterprise CX", "IoT"],
  },
  {
    key: "architect",
    tag: "TECHNICAL ARCHITECT",
    accent: "#00D4AA",
    title: "React, TypeScript, Python, Edge AI. I build the systems.",
    body:
      "Not just wireframes. Production code. Built QuantumEye's architecture from scratch: computer vision pipelines, edge computing, real-time alerting. I speak both design and engineering fluently.",
  },
  {
    key: "founder",
    tag: "FOUNDER & OPERATOR",
    accent: "#D4A843",
    title: "Co-founded and built an AI product from zero to live pilots.",
    body:
      "QuantumEye (2025–2026): from idea to the Retail Technology Show floor. Managed remote engineering teams across the UK and offshore, defined the GTM strategy, ran pilot programmes with retailers, and built the commercial model. Built the company, not just the product. Now building Purple Luna, an AI receptionist for small businesses.",
  },
  {
    key: "strategist",
    tag: "COMMERCIAL STRATEGIST",
    accent: "#0066FF",
    title: "GTM, pilot programmes, market sizing, commercial models.",
    body:
      "I think in business outcomes, not deliverables. Every product decision is filtered through \"will someone pay for this?\" Built go-to-market strategies, commercial models, and the operational frameworks to make them stick.",
  },
];

export const PROOF_INTRO = {
  kicker: "PROOF OF EXECUTION",
  title: "Ventures I've built.",
  sub: "Not case studies. Companies. Products. Teams.",
};

export const VENTURES = [
  {
    key: "quantumeye",
    name: "QuantumEye",
    badge: "2025 — 2026",
    badgeColor: "slate",
    description:
      "The AI-powered retail security platform I co-founded and built: real-time theft detection, repeat-offender recognition and automated staff alerting, all running on edge computing. No cloud round-trip, no privacy compromise.",
    tags: ["Edge AI", "Computer Vision", "React", "Python"],
    role: "Co-Founder, Product & Tech Lead",
    proof: [
      "Exhibited at Retail Tech Show 2025, Int'l Security Expo 2025 & 2026",
      "Retail partnerships with great feedback from early pilots",
      "Code-first build: skipped Figma, shipped a full component library in code",
    ],
    stats: [
      { label: "Detection speed", value: "< 3.9s" },
      { label: "Expos exhibited", value: "2" },
      { label: "Retail pilots",   value: "3" },
      { label: "Cloud round-trips", value: "Zero" },
    ],
    icon: "eye",
    featured: true,
    accent: "#00D4AA",
    image: "/images/QuantumEyeHero.jpg",
    link: "/case/quantumeye",
  },
  {
    key: "purpleluna",
    name: "Purple Luna",
    badge: "ACTIVE",
    badgeColor: "blue",
    description:
      "An AI receptionist for appointment businesses — salons, dental practices, clinics, gyms and more. Luna answers your WhatsApp, books appointments and takes deposits, 24/7, in your own voice. We started as a digital agency; now we've productised that craft for the AI era.",
    stats: [
      { label: "Answers",   value: "24/7" },
      { label: "Lives on",  value: "WhatsApp" },
      { label: "Built for", value: "Any appointment business" },
    ],
    role: "Founder",
    icon: "network",
    featured: false,
    accent: "#8B5CF6",
    image: "/images/purple-luna-card.webp",
    links: [{ label: "Case study", href: "/case/purpleluna" }],
  },
  {
    key: "whotnow",
    name: "WhotNow",
    badge: "GOOD LESSONS",
    badgeColor: "slate",
    description:
      "Real-time discovery platform for nightlife, restaurants, and cafes. Started in the Brighton nightlife scene, expanded to venues across Brighton & Hove. Built the product from scratch, led the team, and got a proper education in what product-market fit actually means in the wild.",
    stats: [
      { label: "Venues onboarded",  value: "200+" },
      { label: "Peak monthly actives", value: "30K" },
      { label: "Coverage",           value: "Brighton, Kent" },
    ],
    role: "Co-Founder & Product Lead",
    icon: "city",
    featured: false,
    accent: "#FF6B35",
    image: "/images/whotnow.webp",
    links: [
      { label: "Mobile App", href: "https://www.youtube.com/watch?v=x7xhbObgXgE" },
      { label: "Venue CMS", href: "https://www.youtube.com/watch?v=YOvQ85YjhFI" },
    ],
  },
];

export const PRINCIPLES = [
  {
    n: "01",
    title: "Ship first, polish later.",
    desc:
      "The best strategy is a live product with real users. I'd rather have something imperfect in people's hands than a perfect deck in a drawer. Process follows product, not the other way around.",
  },
  {
    n: "02",
    title: "If nobody would pay for it, it's not a product.",
    desc:
      "Every feature, every design decision, every sprint gets filtered through one question: does this move the needle commercially? Beautiful design that doesn't convert is just art hanging in a hallway.",
  },
  {
    n: "03",
    title: "Own the full stack or don't bother.",
    desc:
      "Design, engineering, GTM. I don't hand off and hope for the best. The gap between strategy and execution is where most startups quietly die. I stay in the room from whiteboard to deployment.",
  },
  {
    n: "04",
    title: "Get the people right and the code follows.",
    desc:
      "I've managed remote engineering teams across UK and offshore. Hiring well, communicating clearly, and removing blockers matters more than any technical decision you'll ever make.",
  },
  {
    n: "05",
    title: "Taste compounds.",
    desc:
      "Every product decision is either a deposit or a withdrawal from your brand. I take taste seriously — in UI, in copy, in architecture. Small decisions stack up. People notice.",
  },
  {
    n: "06",
    title: "Boring infrastructure, interesting products.",
    desc:
      "Boring CI/CD. Boring standups. Boring deployment pipelines. That's the point. When the infrastructure is invisible, the team can focus on building things people actually care about.",
  },
];

export const SIGNAL = {
  kicker: "LET'S BUILD",
  headline: "Operator mindset. Founder speed. Product discipline.",
  body:
    "Building something and need someone who can own the product, the tech, and the growth strategy? I work with founders as co-founder, fractional CTO, or advisor. Drop me a line.",
  availability: "1–2 advisory slots open · select engagements only",
  ctas: [
    { label: "Start a Conversation", href: "mailto:sam@erpik.com", primary: true, testid: "cta-conversation" },
    { label: "Connect on LinkedIn", href: "https://linkedin.com/in/samerpik/", primary: false, testid: "cta-linkedin" },
  ],
  footerLinks: [
    { label: "LinkedIn", href: "https://linkedin.com/in/samerpik/" },
    { label: "Email", href: "mailto:sam@erpik.com" },
    { label: "Purple Luna", href: "https://purpleluna.co.uk" },
  ],
};

// Chapter scroll progress ranges (0..1) — global scrollYProgress
export const STAGES = {
  hook:       [0.00, 0.07],
  origin:     [0.07, 0.22],
  designer:   [0.22, 0.30],
  architect:  [0.30, 0.38],
  founder:    [0.38, 0.46],
  strategist: [0.46, 0.54],
  proof:      [0.54, 0.76],
  operate:    [0.76, 0.90],
  signal:     [0.90, 1.00],
};
